import { z } from "zod";

import { catalogStatusSchema } from "./admin.js";
import { isoDateTimeSchema, uuidSchema } from "./primitives.js";
import { designerSchema, workDetailSchema, workKindSchema, workSummarySchema } from "./works.js";

/**
 * 后台的作品、设计师与首页内容。
 *
 * 与商品一样，**后台是唯一写入端**：作品的材料清单、首页文案与图片都在这里改，
 * 小程序只读。
 */

// —— 设计师 ——

export const createDesignerRequestSchema = z.object({
  name: z.string().min(1).max(32),
  bio: z.string().max(120),
  /** 评分按十分之一分存（49 表示 4.9）。没有真实评分就留空，不编造。 */
  ratingTenths: z.int().min(0).max(50).nullable(),
});

export type CreateDesignerRequestPayload = z.infer<typeof createDesignerRequestSchema>;

export const updateDesignerRequestSchema = createDesignerRequestSchema.partial().extend({
  avatarPath: z.string().min(1).nullable().optional(),
  status: catalogStatusSchema.optional(),
  sortOrder: z.int().optional(),
});

export type UpdateDesignerRequestPayload = z.infer<typeof updateDesignerRequestSchema>;

export const designerListResponseSchema = z.object({
  items: z.array(designerSchema.extend({ status: catalogStatusSchema, sortOrder: z.int() })),
  total: z.int().nonnegative(),
});

export type DesignerListResponsePayload = z.infer<typeof designerListResponseSchema>;

// —— 作品 ——

/** 材料清单里的一行。价格由服务端按目录算，后台只填用哪一档、几颗。 */
export const workMaterialInputSchema = z.object({
  skuId: uuidSchema,
  quantity: z.int().min(1).max(50),
});

export type WorkMaterialInputPayload = z.infer<typeof workMaterialInputSchema>;

export const createWorkRequestSchema = z.object({
  title: z.string().min(1).max(40),
  kind: workKindSchema,
  description: z.string().max(600),
  designerId: uuidSchema,
  materials: z.array(workMaterialInputSchema).min(1).max(40),
});

export type CreateWorkRequestPayload = z.infer<typeof createWorkRequestSchema>;

export const updateWorkRequestSchema = z.object({
  title: z.string().min(1).max(40).optional(),
  kind: workKindSchema.optional(),
  description: z.string().max(600).optional(),
  designerId: uuidSchema.optional(),
  materials: z.array(workMaterialInputSchema).min(1).max(40).optional(),
  coverPath: z.string().min(1).nullable().optional(),
  photoPaths: z.array(z.string().min(1)).max(9).optional(),
  status: catalogStatusSchema.optional(),
  sortOrder: z.int().optional(),
  /** 是否出现在首页推荐位。 */
  featuredOnHome: z.boolean().optional(),
});

export type UpdateWorkRequestPayload = z.infer<typeof updateWorkRequestSchema>;

/** 后台作品列表的筛选与分页。 */
export const adminWorkListQuerySchema = z.object({
  keyword: z.string().min(1).max(40).optional(),
  kind: workKindSchema.optional(),
  status: catalogStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type AdminWorkListQueryPayload = z.infer<typeof adminWorkListQuerySchema>;

/** 后台看到的作品：在详情之上加上管理与推荐位字段。 */
export const adminWorkSchema = workDetailSchema.extend({
  designerId: uuidSchema,
  status: catalogStatusSchema,
  sortOrder: z.int(),
  featuredOnHome: z.boolean(),
  updatedAt: isoDateTimeSchema,
});

export type AdminWorkPayload = z.infer<typeof adminWorkSchema>;

export const adminWorkListResponseSchema = z.object({
  items: z.array(adminWorkSchema),
  total: z.int().nonnegative(),
});

export type AdminWorkListResponsePayload = z.infer<typeof adminWorkListResponseSchema>;

/** 小程序首页的推荐位：上架且标了推荐的作品，按排序取前几个。 */
export const featuredWorksResponseSchema = z.object({
  items: z.array(workSummarySchema),
});

export type FeaturedWorksResponsePayload = z.infer<typeof featuredWorksResponseSchema>;

// —— 首页内容 ——

/**
 * 整份替换首页内容。
 *
 * 主页内容是一份整体（轮播 + 三组步骤），逐项增删改的接口会让后台写起来更复杂，
 * 收益却很小：运营改完一次点保存，服务端在一个事务里整份换掉。
 * 请求里不带 id —— 编号由服务端生成。
 */
const slideInputSchema = z.object({
  imagePath: z.string().min(1),
  tag: z.string().max(20),
  title: z.string().min(1).max(40),
  subtitle: z.string().max(120),
});

const stepInputSchema = z.object({
  label: z.string().max(20),
  title: z.string().min(1).max(30),
  subtitle: z.string().max(120),
});

export const updateHomeContentRequestSchema = z.object({
  slides: z.array(slideInputSchema).max(5),
  workshopSteps: z.array(stepInputSchema).max(6),
  highlights: z.array(stepInputSchema).max(6),
  craftingSteps: z.array(stepInputSchema).max(6),
});

export type UpdateHomeContentRequestPayload = z.infer<typeof updateHomeContentRequestSchema>;
